import { Component, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ResetPasswordValidators } from "src/app/share/class/reset-password-validators";
import { UserDataService } from "src/app/share/data-service/user.data.service";
import { InfoDialogComponent } from "src/app/share/dialog/info-dialog/info-dialog.component";
import { AlertService } from "src/app/share/service/alert.service";
import { AuthService } from "src/app/share/service/auth.service";
import { LoadingService } from "src/app/share/service/loading.service";
import { PartnerService } from "src/app/share/service/partner.service";

@Component({
	selector: "app-reset-password",
	templateUrl: "./reset-password.component.html",
	styleUrls: ["./reset-password.component.scss"],
})
export class ResetPasswordComponent implements OnInit {
	hide: boolean = true;
	hideNew: boolean = true;
	expired: boolean = false;
	success: boolean = false;
	submitted: boolean = false;
	newPassLength: boolean = false;
	repeatNewPassLength: boolean = false;

	key_activation: any;
	resetPasswordFormGroup: UntypedFormGroup;
	partnerCode: string = 'ps';

	constructor(
		private dialog: MatDialog,
		public router: Router,
		public authService: AuthService,
		public alertService: AlertService,
		public translate: TranslateService,
		public activatedRoute: ActivatedRoute,
		private userDataService: UserDataService,
		private loadingService: LoadingService,
		private partnerService: PartnerService
	) {
		this.partnerCode = this.partnerService.getPartnerCode();
		if(!this.partnerCode) {
			this.partnerCode = "ps";
		} 
	}

	ngOnInit(): void {
		this.resetPasswordFormGroup = new UntypedFormGroup(
			{
				newPassword: new UntypedFormControl(
					null,
					Validators.compose([
						Validators.required,
						Validators.minLength(8),
						ResetPasswordValidators.patternValidator(
							new RegExp("(?=.*[0-9])"),
							{
								requiresDigit: true,
							},
						),
						ResetPasswordValidators.patternValidator(
							new RegExp("(?=.*[a-z])"),
							{
								requiresLowercase: true,
							},
						),
						ResetPasswordValidators.patternValidator(
							new RegExp("(?=.*[$@^!%*?&])"),
							{
								requiresSpecialChars: true,
							},
						),
					]),
				),
				repeatNewPassword: new UntypedFormControl(null, [
					Validators.required,
					Validators.minLength(8),
				]),
			},
			{
				validators: ResetPasswordValidators.MatchValidator,
			},
		);
	}

	// convenience getter for easy access to form controls
	get f() {
		return this.resetPasswordFormGroup.controls;
	}

	get passwordValid() {
		return this.resetPasswordFormGroup.controls["newPassword"].errors === null;
	}

	get requiredValid() {
		return !this.resetPasswordFormGroup.controls["newPassword"].hasError(
			"required",
		);
	}

	get minLengthValid() {
		return !this.resetPasswordFormGroup.controls["newPassword"].hasError(
			"minlength",
		);
	}

	get requiresDigitValid() {
		return !this.resetPasswordFormGroup.controls["newPassword"].hasError(
			"requiresDigit",
		);
	}

	get requiresLowercaseValid() {
		return !this.resetPasswordFormGroup.controls["newPassword"].hasError(
			"requiresLowercase",
		);
	}

	get requiresSpecialCharsValid() {
		return !this.resetPasswordFormGroup.controls["newPassword"].hasError(
			"requiresSpecialChars",
		);
	}

	get isCarsome() {
		return this.partnerCode == 'carsome';
	}

	private errorDialog() {
		this.dialog.open(InfoDialogComponent, {
			width: "400px",
			panelClass: "dialog-container",
			data: {
				img: "../../../assets/image/invalid-icon.png",
				textBold: this.translate.instant("error.resetPassLinkInvalid"),
			},
		});
	}

	private successDialog() {
		let dialogRef = this.dialog.open(InfoDialogComponent, {
			width: "400px",
			panelClass: "dialog-container",
			data: {
				img: "../../../assets/image/success-icon.png",
				textBold: this.translate.instant("auth.successfullChange"),
			},
		});

		dialogRef.afterClosed().subscribe((x) => {
			// Navigate to login page
			if(this.partnerCode == 'ps'){
				this.router.navigate(["/login"]);
			} else {
				this.router.navigate(["/login"], { queryParams: { partnerCode: this.partnerCode } });
			}
		});
	}

	check_url_validation(key?: any) {
		this.userDataService.resetPasswordLinkVerify(key).subscribe({
			next: (x) => {
				if (x) {
					this.expired = false;
				} else {
					this.expired = true;
				}
			},
			error: (err) => {
				this.errorDialog();
			},
		});
	}

	submit() {
		this.submitted = true;

		if (this.resetPasswordFormGroup.invalid) {
			return;
		}

		let new_password = this.resetPasswordFormGroup.get("newPassword").value;
		let confirmedNewPassword =
			this.resetPasswordFormGroup.get("repeatNewPassword").value;

		let encrypted_key = this.router.url.substring(
			this.router.url.lastIndexOf("=") + 1,
		);

		let objRequest = {
			newPassword: encodeURIComponent(new_password),
			confirmNewPassword: encodeURIComponent(confirmedNewPassword),
			key: decodeURIComponent(encrypted_key),
		};

		this.loadingService.loadingOn(true);
		this.userDataService.resetPassword(objRequest).subscribe({
			next: (x) => {
				this.loadingService.loadingOff(true);
				this.success = true;
				this.successDialog();
			},
			error: (err) => {
				this.loadingService.loadingOff(true);
				this.errorDialog();
			},
		});
	}

	getNewPasswordLength(value: any) {
		if (value.length < 8) {
			this.newPassLength = false;
		} else {
			this.newPassLength = true;
		}
	}

	getRepeatNewPasswordLength(value: any) {
		if (value.length < 8) {
			this.repeatNewPassLength = false;
		} else {
			this.repeatNewPassLength = true;
		}
	}
}
